// =============================================================================
// FamilySection.js — Phase 82
//
// Family & Sharing section within the new /settings sidebar. Shows:
//   • For non-Summit plans → Figma's upsell card with feature preview list.
//   • For Summit plans → active workspace summary + pending invites +
//     members list + "Open full family management" deep link.
//
// Wired to the existing workspace API helpers in lib/workspaces.js. Complex
// invite-code and audit-log flows still live in the dedicated /settings/family
// page which users can jump to with one click.
// =============================================================================
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { effectivePlan } from '@/lib/entitlements';
import { listMyWorkspaces, listMembers, inviteMember, removeMember } from '@/lib/workspaces';
import { SectionHeader, SettingsCard, PrimaryButton, TextInput, FieldLabel } from './primitives';

const PREVIEW_ROWS = [
  ['Shared net worth', 'Optional'],
  ['Goal progress', 'Optional'],
  ['Monthly summary', 'Always shown'],
  ['Transaction details', 'Never shared'],
  ['Account balances', 'Summary only'],
];

export default function FamilySection() {
  const { profile, session } = useAuth();
  const nav = useNavigate();
  const eff = effectivePlan(profile);
  const isSummit = eff === 'summit';

  const [workspace, setWorkspace] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);

  const load = useCallback(async () => {
    if (!session?.access_token) return;
    setLoading(true);
    try {
      const ws = await listMyWorkspaces(session.access_token);
      // Phase 84.5 — Family & Sharing must also work for non-owner members
      // (partners / accountants) who joined a shared workspace. Prefer any
      // 'shared' workspace the viewer belongs to, then fall back to a
      // personal one the viewer owns. Preserves the previous owner-only
      // behaviour where the viewer created their own household.
      const workspaces = ws.workspaces || [];
      const sharedAsOwner = workspaces.find((w) => w.is_owner && w.kind === 'shared');
      const sharedAsMember = workspaces.find((w) => !w.is_owner && w.kind === 'shared');
      const personal = workspaces.find((w) => w.is_owner && w.kind === 'personal');
      const active = sharedAsOwner || sharedAsMember || personal;
      setWorkspace(active);
      if (active) {
        const m = await listMembers(session.access_token, active.id);
        setMembers(m.members || []);
      }
    } catch (e) {
      console.warn('[family] load failed:', e);
    } finally {
      setLoading(false);
    }
  }, [session?.access_token]);

  useEffect(() => { load(); }, [load]);

  const submitInvite = async () => {
    const email = (inviteEmail || '').trim().toLowerCase();
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address.');
      return;
    }
    if (!workspace?.id) {
      toast.error('No active workspace found.');
      return;
    }
    setInviting(true);
    const res = await inviteMember(session.access_token, workspace.id, email, 'partner');
    setInviting(false);
    if (res?.ok) {
      setInviteEmail('');
      toast.success('Invite sent.', { description: `Manage full invite flow in /settings/family.` });
      load();
    } else {
      toast.error(res?.error || 'Could not send the invite.');
    }
  };

  const onRemove = async (member) => {
    if (!workspace?.id) return;
    const res = await removeMember(session.access_token, workspace.id, member.id);
    if (res?.ok) {
      toast.success('Member removed.');
      load();
    } else {
      toast.error(res?.error || 'Could not update membership.');
    }
  };

  // ─── Non-Summit upsell ────────────────────────────────────────────────
  if (!isSummit) {
    return (
      <div className="space-y-5" data-testid="settings-family-section">
        <SectionHeader
          title="Family & Sharing"
          subtitle="Invite family members to view a shared financial summary, or manage existing access."
        />

        <SettingsCard className="px-6 py-5">
          <div className="flex items-start justify-between gap-4 flex-wrap sm:flex-nowrap">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-charcoal">2120 Summit plan</p>
              <p className="text-sm text-muted-foreground mt-0.5">
                Share a summarised financial view with your partner. Each member sees only what you choose to share.
              </p>
            </div>
            <PrimaryButton variant="primary" size="sm" onClick={() => nav('/pricing')} testId="settings-family-upgrade">
              View plans
            </PrimaryButton>
          </div>
          <div className="mt-5 p-4 rounded-xl bg-offwhite border border-border/50">
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground/70 mb-3">
              What family members can see
            </p>
            <div className="space-y-2">
              {PREVIEW_ROWS.map(([label, note]) => (
                <div key={label} className="flex items-center justify-between">
                  <span className="text-sm text-charcoal/80">{label}</span>
                  <span className="text-xs text-muted-foreground">{note}</span>
                </div>
              ))}
            </div>
          </div>
        </SettingsCard>

        <SettingsCard className="px-6 py-5">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70 mb-4">Pending invitations</p>
          <div className="text-center py-6">
            <p className="text-sm text-muted-foreground/70">No invitations sent yet.</p>
            <p className="text-xs text-muted-foreground/50 mt-2">Available on Summit plan.</p>
          </div>
        </SettingsCard>
      </div>
    );
  }

  // ─── Summit: real workspace management ───────────────────────────────
  return (
    <div className="space-y-5" data-testid="settings-family-section">
      <SectionHeader
        title="Family & Sharing"
        subtitle="Manage members of your shared workspace. Deeper controls (invite codes, audit log) live on the full family page."
      />

      <SettingsCard className="px-6 py-5">
        <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
          <div>
            <p className="text-sm font-semibold text-charcoal">{workspace?.name || 'Your workspace'}</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {members.length} member{members.length === 1 ? '' : 's'} · {workspace?.kind === 'shared' ? 'Shared' : 'Personal'}
            </p>
          </div>
          <PrimaryButton variant="ghost" size="sm" onClick={() => nav('/settings/family')} testId="settings-family-open-full">
            Open full family page
          </PrimaryButton>
        </div>

        {loading ? (
          <div className="space-y-2">
            <div className="h-10 bg-offwhite/60 rounded-lg animate-pulse" />
            <div className="h-10 bg-offwhite/60 rounded-lg animate-pulse" />
          </div>
        ) : (
          <div className="space-y-0">
            {members.map((m, i) => {
              // Phase 84.5 — Remove button visibility rules:
              //   • Only the workspace owner can revoke other members.
              //   • The owner is never removable (role === 'owner').
              //   • Non-owner viewers see the roster but no Remove
              //     controls (not on themselves, not on the owner).
              // Backend already enforces this at DELETE /api/workspaces/
              // {ws_id}/members/{member_id}; hiding the button here is a
              // UX-consistency layer so viewers don't see actions that
              // would 403 on them.
              const viewerIsOwner = Boolean(workspace?.is_owner);
              const memberIsOwner = (m.role || '').toLowerCase() === 'owner';
              const canRemove = viewerIsOwner && !memberIsOwner;
              return (
              <div
                key={m.id}
                className="flex items-center gap-3 py-3"
                style={{ borderTop: i > 0 ? '1px solid hsl(var(--border) / 0.5)' : 'none' }}
                data-testid={`settings-family-member-${i}`}
              >
                <div className="w-8 h-8 rounded-full bg-evergreen/10 text-evergreen flex items-center justify-center text-xs font-semibold">
                  {(m.display_name || m.email || '?').charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-charcoal truncate">{m.display_name || m.email}</p>
                  <p className="text-xs text-muted-foreground">{m.role || 'member'}</p>
                </div>
                {canRemove && (
                <button
                  type="button"
                  onClick={() => onRemove(m)}
                  className="text-xs text-muted-foreground/70 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                  data-testid={`settings-family-remove-${i}`}
                >
                  Remove
                </button>
                )}
              </div>
              );
            })}
          </div>
        )}
      </SettingsCard>

      {/* Phase 84.5 — Only the workspace owner can invite. Non-owner
          viewers see the roster but no invite affordance. */}
      {workspace?.is_owner && (
      <SettingsCard className="px-6 py-5">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70 mb-4">Invite a family member</p>
        <div className="flex items-end gap-3 flex-wrap">
          <div className="flex-1 min-w-[220px]">
            <FieldLabel label="Email address" />
            <TextInput
              value={inviteEmail}
              onChange={setInviteEmail}
              placeholder="partner@email.com"
              type="email"
              testId="settings-family-invite-email"
            />
          </div>
          <PrimaryButton
            onClick={submitInvite}
            disabled={inviting || !inviteEmail}
            testId="settings-family-invite-submit"
          >
            {inviting ? 'Sending…' : 'Send invite'}
          </PrimaryButton>
        </div>
        <p className="text-xs text-muted-foreground/70 mt-3">
          For invite codes and copy-link options, use the full family page.
        </p>
      </SettingsCard>
      )}
    </div>
  );
}
